// ---- Map terrain layers (plan 20) ----
// The authored elevation/scatter data layer over the procedural noise base.
// A map doc (content/maps/<id>.json) composes into heightAt() as:
//
//   base = (fbm(x+ox, z+oz) − 0.42) * AMP*2      // the noise base (terrain.ts)
//   base = foldFeatures(x, z, base)               // PRE-carve parametric ops —
//                                                 // basins/hills get natural
//                                                 // carved shorelines
//   h    = carveShore(base)                       // untouched (terrain.ts)
//   h   += brushDelta(x, z)                       // POST-carve hand sculpting —
//                                                 // WYSIWYG, never remapped
//   return applyFlatten(x, z, h)                  // map flats + structure pads
//
// PURE + DOM-free + THREE-free + Bun-free + content-free: this module imports
// NOTHING at runtime (type-only schema types), like src/sim/*. The server folds
// it into the headless world build, the client and the studio fold the SAME
// data — client/server byte parity is load-bearing (see terrain.ts).
//
// Module state (setMapTerrain) mirrors flatten.ts's setStructureFlatZones:
// hydrated once at content load (src/content/load.ts) and by the studio's
// world editor while sculpting. All the math is ALSO exported pure (data as
// arguments) for tests and the editor's what-if previews.

import type { MapDoc } from "../content/schemas.ts";

export type MapTerrainDoc = MapDoc["terrain"];
export type MapFeatureDoc = MapTerrainDoc["features"][number];
export type MapFlatDoc = MapTerrainDoc["flats"][number];
export type MapBrushDoc = NonNullable<MapTerrainDoc["brush"]>;
export type ScatterMaskDoc = NonNullable<MapDoc["scatterMasks"]>[number];

/** Feather applied when a feature/mask omits one. */
export const FEATHER_DEFAULT = 8;

/** Default brush cell size, world units. 2.5 matches the terrain mesh's vertex
 *  spacing at WORLD_SIZE 1080 (chunk cw=135, seg=54 — terrain.ts), so brush
 *  grid points coincide with render verts and sculpting lands exactly. */
export const BRUSH_RES_DEFAULT = 2.5;

// ---------------------------------------------------------------------------
// Falloff
// ---------------------------------------------------------------------------

/** Falloff weight: 1 inside `r`, smoothstep down to 0 across `feather`. The
 *  FlatZone blend (flatten.ts) generalized — continuous at both band edges. */
export function blendW(d: number, r: number, feather: number): number {
  if (d <= r) return 1;
  if (d >= r + feather) return 0;
  const t = (d - r) / feather;
  return 1 - t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// Features — parametric elevation ops, folded SEQUENTIALLY in array order
// (plateau blends toward an absolute elevation, so order matters; documented
// in SCHEMA.md).
// ---------------------------------------------------------------------------

export type Bounds = { minX: number; maxX: number; minZ: number; maxZ: number };
export type HydratedFeature = { f: MapFeatureDoc } & Bounds;

/** AABB of a feature's influence (core + feather) — the per-call early-out. */
export function featureBounds(f: MapFeatureDoc): Bounds {
  const feather = f.feather ?? FEATHER_DEFAULT;
  switch (f.kind) {
    case "hill":
    case "basin":
    case "plateau": {
      const e = f.r + feather;
      return { minX: f.x - e, maxX: f.x + e, minZ: f.z - e, maxZ: f.z + e };
    }
    case "ridge": {
      const e = f.w / 2 + feather;
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
      for (const [px, pz] of f.points) {
        minX = Math.min(minX, px); maxX = Math.max(maxX, px);
        minZ = Math.min(minZ, pz); maxZ = Math.max(maxZ, pz);
      }
      return { minX: minX - e, maxX: maxX + e, minZ: minZ - e, maxZ: maxZ + e };
    }
    case "ramp": {
      const e = f.w / 2 + feather;
      return {
        minX: Math.min(f.x0, f.x1) - e, maxX: Math.max(f.x0, f.x1) + e,
        minZ: Math.min(f.z0, f.z1) - e, maxZ: Math.max(f.z0, f.z1) + e,
      };
    }
  }
}

export function hydrateFeatures(fs: MapFeatureDoc[]): HydratedFeature[] {
  return fs.map((f) => ({ f, ...featureBounds(f) }));
}

/** Distance from (x,z) to the segment (x0,z0)→(x1,z1), plus the clamped
 *  projection parameter t (0..1) — ramps lerp height along t. Shared with
 *  world.ts's road-clearance tests. Returns a REUSED scratch (the npcStrike
 *  idiom) — segment math runs in the scatter loop's ~900k-call hot path, so
 *  consume synchronously, never retain. */
const _seg = { d: 0, t: 0 };
export function segDist(x: number, z: number, x0: number, z0: number, x1: number, z1: number): { d: number; t: number } {
  const dx = x1 - x0, dz = z1 - z0;
  const len2 = dx * dx + dz * dz;
  const t = len2 ? Math.min(1, Math.max(0, ((x - x0) * dx + (z - z0) * dz) / len2)) : 0;
  _seg.d = Math.hypot(x - (x0 + dx * t), z - (z0 + dz * t));
  _seg.t = t;
  return _seg;
}

/** Fold every feature into a base height. Sequential; AABB-rejected per call. */
export function foldFeatures(x: number, z: number, base: number, fs: HydratedFeature[]): number {
  for (const hf of fs) {
    if (x < hf.minX || x > hf.maxX || z < hf.minZ || z > hf.maxZ) continue;
    const f = hf.f;
    const feather = f.feather ?? FEATHER_DEFAULT;
    switch (f.kind) {
      case "hill":
        base += f.h * blendW(Math.hypot(x - f.x, z - f.z), f.r, feather);
        break;
      case "basin":
        base -= f.depth * blendW(Math.hypot(x - f.x, z - f.z), f.r, feather);
        break;
      case "plateau": {
        // Blend TOWARD the elevation (not additive) — flat top at exactly f.y.
        const w = blendW(Math.hypot(x - f.x, z - f.z), f.r, feather);
        base += (f.y - base) * w;
        break;
      }
      case "ridge": {
        let d = Infinity;
        for (let i = 0; i + 1 < f.points.length; i++) {
          const [x0, z0] = f.points[i]!;
          const [x1, z1] = f.points[i + 1]!;
          d = Math.min(d, segDist(x, z, x0, z0, x1, z1).d);
        }
        base += f.h * blendW(d, f.w / 2, feather);
        break;
      }
      case "ramp": {
        const { d, t } = segDist(x, z, f.x0, f.z0, f.x1, f.z1); // destructure = consume synchronously
        base += (f.h0 + (f.h1 - f.h0) * t) * blendW(d, f.w / 2, feather);
        break;
      }
    }
  }
  return base;
}

// ---------------------------------------------------------------------------
// Brush — the sparse hand-sculpt delta grid. Doc cells are keyed "ix,iz"
// (world pos = ix*res, iz*res); hydrated into a Map with a packed numeric key
// for O(1) lookups. Bilinear between grid points; absent cell = 0.
// ---------------------------------------------------------------------------

// Packed key spans ±2048 cells — at res 2.5 that's a ±5120u world, far past
// any current map. Out-of-range keys are rejected by validate.ts (bounds).
const KMAX = 2048;
const KSPAN = 4096;

export const packCell = (ix: number, iz: number): number => (ix + KMAX) * KSPAN + (iz + KMAX);
export const cellKey = (ix: number, iz: number): string => `${ix},${iz}`;
export function parseCellKey(key: string): [number, number] {
  const c = key.indexOf(",");
  return [parseInt(key.slice(0, c), 10), parseInt(key.slice(c + 1), 10)];
}

export type BrushGrid = { res: number; cells: Map<number, number> };

export function hydrateBrush(doc?: MapBrushDoc): BrushGrid {
  const cells = new Map<number, number>();
  if (doc) {
    for (const [k, dy] of Object.entries(doc.cells)) {
      const [ix, iz] = parseCellKey(k);
      if (Math.abs(ix) >= KMAX || Math.abs(iz) >= KMAX) continue; // validated upstream; never trust
      cells.set(packCell(ix, iz), dy);
    }
  }
  return { res: doc?.res ?? BRUSH_RES_DEFAULT, cells };
}

export function brushDelta(x: number, z: number, b: BrushGrid): number {
  if (b.cells.size === 0) return 0;
  const gx = x / b.res, gz = z / b.res;
  const ix = Math.floor(gx), iz = Math.floor(gz);
  const fx = gx - ix, fz = gz - iz;
  const d00 = b.cells.get(packCell(ix, iz)) ?? 0;
  const d10 = b.cells.get(packCell(ix + 1, iz)) ?? 0;
  const d01 = b.cells.get(packCell(ix, iz + 1)) ?? 0;
  const d11 = b.cells.get(packCell(ix + 1, iz + 1)) ?? 0;
  return (d00 * (1 - fx) + d10 * fx) * (1 - fz) + (d01 * (1 - fx) + d11 * fx) * fz;
}

// ---------------------------------------------------------------------------
// Scatter masks — region multipliers over the procedural deco scatter. Pure
// only (no module state): world.ts hydrates the active map's masks and passes
// them to the scatter loop / computeScatter. Semantics:
//   clear    density *= (1 − w)                    (feathered clearing)
//   densify  density *= 1 + (factor−1) * w         (default factor 2)
//   bias     pickDeco weight for `deco` *= 1 + (factor−1) * w
// ---------------------------------------------------------------------------

export type HydratedMask = { m: ScatterMaskDoc } & Bounds;

export function maskBounds(m: ScatterMaskDoc): Bounds {
  const feather = m.feather ?? 0;
  if (m.shape === "circle") {
    const e = m.r + feather;
    return { minX: m.x - e, maxX: m.x + e, minZ: m.z - e, maxZ: m.z + e };
  }
  return { minX: m.minX - feather, maxX: m.maxX + feather, minZ: m.minZ - feather, maxZ: m.maxZ + feather };
}

export function hydrateMasks(ms: ScatterMaskDoc[]): HydratedMask[] {
  return ms.map((m) => ({ m, ...maskBounds(m) }));
}

/** Coverage weight of one mask at (x,z): 1 in the core, feathered to 0. */
function maskW(x: number, z: number, m: ScatterMaskDoc): number {
  const feather = m.feather ?? 0;
  if (m.shape === "circle") return blendW(Math.hypot(x - m.x, z - m.z), m.r, feather);
  // Rect: distance OUTSIDE the rect (0 inside), feathered from the edge.
  const dx = Math.max(m.minX - x, 0, x - m.maxX);
  const dz = Math.max(m.minZ - z, 0, z - m.maxZ);
  return blendW(Math.hypot(dx, dz), 0, feather);
}

/** Density multiplier at (x,z) — 1 with no coverage, 0 fully cleared. */
export function maskDensity(x: number, z: number, ms: HydratedMask[]): number {
  let density = 1;
  for (const hm of ms) {
    if (x < hm.minX || x > hm.maxX || z < hm.minZ || z > hm.maxZ) continue;
    const m = hm.m;
    if (m.effect === "bias") continue;
    const w = maskW(x, z, m);
    if (w <= 0) continue;
    if (m.effect === "clear") density *= 1 - w;
    else density *= 1 + ((m.factor ?? 2) - 1) * w;
    if (density <= 0) return 0;
  }
  return density;
}

/** Per-deco-type weight multipliers at (x,z), or null when no bias covers. */
export function maskWeights(x: number, z: number, ms: HydratedMask[]): Record<string, number> | null {
  let out: Record<string, number> | null = null;
  for (const hm of ms) {
    if (hm.m.effect !== "bias" || !hm.m.deco) continue;
    if (x < hm.minX || x > hm.maxX || z < hm.minZ || z > hm.maxZ) continue;
    const w = maskW(x, z, hm.m);
    if (w <= 0) continue;
    out ??= {};
    out[hm.m.deco] = (out[hm.m.deco] ?? 1) * (1 + ((hm.m.factor ?? 2) - 1) * w);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Module state — the ACTIVE map's terrain, set once at content load
// (src/content/load.ts) and re-set by the studio world editor per edit.
// heightAt (terrain.ts) reads through these two accessors every call.
// ---------------------------------------------------------------------------

let activeFeatures: HydratedFeature[] = [];
let activeBrush: BrushGrid = { res: BRUSH_RES_DEFAULT, cells: new Map() };

/** fbm domain shift for the active map — terrain.ts adds it to the sample
 *  coordinates. [0,0] (every current map) samples today's noise byte-identically. */
export const noiseOff = { x: 0, z: 0 };

/** Noise amplitude multiplier for the active map — terrain.ts scales the fbm
 *  base by it. 1 (the default when a doc omits `amp`) is today's terrain
 *  byte-identically; 0 gives a perfectly flat base that features still carve. */
export const noiseAmp = { v: 1 };

export function setMapTerrain(t: MapTerrainDoc): void {
  activeFeatures = hydrateFeatures(t.features);
  activeBrush = hydrateBrush(t.brush);
  noiseOff.x = t.noiseOffset?.[0] ?? 0;
  noiseOff.z = t.noiseOffset?.[1] ?? 0;
  noiseAmp.v = t.amp ?? 1;
}

/** Incrementally patch the ACTIVE brush from `doc` for just `keys` (the cells
 *  a sculpt stamp touched — applyBrushStamp returns them), so a stroke stays
 *  O(touched cells) instead of re-hydrating the whole grid per stamp. Falls
 *  back to a full rehydrate if the doc's res disagrees with the active grid. */
export function patchActiveBrush(doc: MapBrushDoc, keys: string[]): void {
  if ((doc.res ?? BRUSH_RES_DEFAULT) !== activeBrush.res) {
    activeBrush = hydrateBrush(doc);
    return;
  }
  for (const key of keys) {
    const [ix, iz] = parseCellKey(key);
    if (Math.abs(ix) >= KMAX || Math.abs(iz) >= KMAX) continue;
    const dy = doc.cells[key];
    if (dy === undefined) activeBrush.cells.delete(packCell(ix, iz));
    else activeBrush.cells.set(packCell(ix, iz), dy);
  }
}

/** Features folded over `base` at (x,z) — identity while no features exist. */
export function mapFeatureBase(x: number, z: number, base: number): number {
  return activeFeatures.length ? foldFeatures(x, z, base, activeFeatures) : base;
}

/** The hand-sculpt delta at (x,z) — 0 while the brush is empty. */
export function mapBrushDelta(x: number, z: number): number {
  return brushDelta(x, z, activeBrush);
}
